import { useEffect } from 'react';

/**
 * Lightweight SEO component that updates document title, meta description,
 * canonical link, and Open Graph/Twitter tags per route — no external deps.
 *
 * Usage: <SEO title="..." description="..." path="/about" />
 */
const SITE_NAME = 'Meet For Mahjong';
const SITE_ORIGIN = 'https://meetformahjong.com';
// Brand logo at 1200×1200 JPEG ≈150 KB (down from the 7.9 MB hi-res PNG
// the page used to ship). Hosted at the site root so SEO crawlers and
// social previews fetch it directly, no CDN round-trip required.
const DEFAULT_IMAGE = `${SITE_ORIGIN}/logo.jpg`;

function setMeta(selector, attr, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const match = selector.match(/\[(name|property)="([^"]+)"\]/);
    if (match) el.setAttribute(match[1], match[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export default function SEO({ title, description, path = '', image = DEFAULT_IMAGE, keywords, jsonLd }) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    const url = `${SITE_ORIGIN}${path}`;

    document.title = fullTitle;
    if (description) {
      setMeta('meta[name="description"]', 'content', description);
      setMeta('meta[property="og:description"]', 'content', description);
      setMeta('meta[name="twitter:description"]', 'content', description);
    }
    if (keywords) {
      setMeta('meta[name="keywords"]', 'content', keywords);
    }
    setMeta('meta[property="og:title"]', 'content', fullTitle);
    setMeta('meta[property="og:url"]', 'content', url);
    setMeta('meta[property="og:image"]', 'content', image);
    setMeta('meta[name="twitter:title"]', 'content', fullTitle);
    setMeta('meta[name="twitter:image"]', 'content', image);
    setLink('canonical', url);

    // JSON-LD structured data — every route emits a baseline `WebPage`
    // schema describing the current URL, title, and description. This
    // gives Google an unambiguous "this is a named, real page with
    // substance" signal and helps prevent Search Console flagging the
    // page as a "soft 404". Routes can also pass a custom `jsonLd`
    // object (or array) that gets merged into the same @graph array.
    const existing = document.head.querySelectorAll('script[data-seo-jsonld="1"]');
    existing.forEach((el) => el.remove());

    const baseWebPage = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: fullTitle,
      description: description || undefined,
      isPartOf: {
        '@type': 'WebSite',
        name: SITE_NAME,
        url: SITE_ORIGIN,
      },
      primaryImageOfPage: image ? { '@type': 'ImageObject', url: image } : undefined,
      inLanguage: 'en-US',
    };
    // Strip undefined keys so the JSON-LD stays clean.
    Object.keys(baseWebPage).forEach((k) => baseWebPage[k] === undefined && delete baseWebPage[k]);

    const blobs = [baseWebPage];
    if (jsonLd) {
      // Accept either a single object or an array of objects.
      if (Array.isArray(jsonLd)) blobs.push(...jsonLd);
      else blobs.push(jsonLd);
    }

    blobs.forEach((blob) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-jsonld', '1');
      script.textContent = JSON.stringify(blob);
      document.head.appendChild(script);
    });

    return () => {
      // Clean up our JSON-LD on unmount so a stale schema doesn't
      // leak into a route that doesn't pass one.
      const stale = document.head.querySelectorAll('script[data-seo-jsonld="1"]');
      stale.forEach((el) => el.remove());
    };
  }, [title, description, path, image, keywords, jsonLd]);

  return null;
}
